import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import './MeetingForwardRequestsModal.css';
import { notify } from '../CustomToast';
import meetingService from '../../services/meetingService';
import ForwardRequestsListSkeleton from './ForwardRequestsListSkeleton';

const MeetingForwardRequestsModal = ({ isOpen, onClose, meeting, isOrganizerView = true }) => {
    const { t } = useI18n();
    const [requests, setRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        if (isOpen && meeting?.id) {
            if (isOrganizerView) {
                fetchRequests('pending');
            } else {
                fetchMyRequests();
            }
        }
    }, [isOpen, meeting?.id, isOrganizerView]);

    const fetchRequests = async (status = 'pending') => {
        setLoading(true);
        setActiveTab(status);
        setSelectedRequest(null);
        setRejectionReason('');

        try {
            const response = await meetingService.getForwardRequests(meeting.id, status);
            if (Array.isArray(response)) {
                setRequests(response);
            } else if (Array.isArray(response.data)) {
                setRequests(response.data);
            } else {
                setRequests([]);
            }
            // Auto-select first request
            if (response.data && response.data.length > 0) {
                setSelectedRequest(response.data[0]);
            }
        } catch (err) {
            console.error('Error fetching forward requests:', err);
            notify('error', err.message || t('common.messages.error_loading_meetings') || 'Failed to load forward requests');
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchMyRequests = async () => {
        setLoading(true);
        setSelectedRequest(null);
        setRejectionReason('');

        try {
            const response = await meetingService.getMyForwardRequests(meeting.id);
            if (Array.isArray(response)) {
                setRequests(response);
            } else if (Array.isArray(response.data?.forward_requests)) {
                setRequests(response.data.forward_requests);
            } else {
                setRequests([]);
            }
            // Auto-select first (and only) request
            if (response.data?.forward_requests && response.data.forward_requests.length > 0) {
                setSelectedRequest(response.data.forward_requests[0]);
            }
        } catch (err) {
            console.error('Error fetching my forward requests:', err);
            notify('error', err.message || t('common.messages.error_loading_meetings') || 'Failed to load forward requests');
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!selectedRequest) return;

        try {
            await meetingService.acceptForwardRequest(meeting.id, selectedRequest.id);
            notify('success', t('pages.meetings.forward_request_accepted') || 'Forward request accepted');
            await fetchRequests(activeTab);
        } catch (err) {
            console.error('Error accepting forward request:', err);
            notify('error', err.message || 'Failed to accept forward request');
        }
    };

    const handleReject = async () => {
        if (!selectedRequest) return;

        try {
            await meetingService.rejectForwardRequest(meeting.id, selectedRequest.id, rejectionReason || null);
            notify('success', t('pages.meetings.forward_request_rejected') || 'Forward request rejected');
            await fetchRequests(activeTab);
        } catch (err) {
            console.error('Error rejecting forward request:', err);
            notify('error', err.message || 'Failed to reject forward request');
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'accepted':
                return 'mfrm-status-accepted';
            case 'rejected':
                return 'mfrm-status-rejected';
            case 'pending':
            default:
                return 'mfrm-status-pending';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'accepted':
                return t('pages.meetings.status_accepted') || 'Accepted';
            case 'rejected':
                return t('pages.meetings.status_rejected') || 'Rejected';
            case 'pending':
            default:
                return t('pages.meetings.status_pending') || 'Pending';
        }
    };

    const getScopeLabel = (scope) => {
        return scope === 'entire_series'
            ? (t('pages.meetings.entire_series') || 'Entire Series')
            : (t('pages.meetings.just_this_one') || 'Just This One');
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="mfrm-overlay" onClick={handleBackdropClick}>
            <div className="mfrm-modal-container">
                {/* Header */}
                <div className="mfrm-header-styled">
                    <h2 className="mfrm-title-styled">
                        {t('pages.meetings.forward_requests') || 'Forward Requests'}
                    </h2>
                    <button className="mfrm-close-btn-styled" onClick={onClose} aria-label="Close">
                        <FaTimes size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="mfrm-body">
                    <div className="mfrm-container">
                        {isOrganizerView && (
                            <div className="mfrm-left-panel">
                                <div className="mfrm-filter-tabs">
                                    <button
                                        className={`mfrm-filter-tab ${activeTab === 'pending' ? 'active' : ''}`}
                                        onClick={() => fetchRequests('pending')}
                                    >
                                        {t('pages.meetings.status_pending') || 'Pending'}
                                    </button>
                                    <button
                                        className={`mfrm-filter-tab ${activeTab === 'accepted' ? 'active' : ''}`}
                                        onClick={() => fetchRequests('accepted')}
                                    >
                                        {t('pages.meetings.status_accepted') || 'Accepted'}
                                    </button>
                                    <button
                                        className={`mfrm-filter-tab ${activeTab === 'rejected' ? 'active' : ''}`}
                                        onClick={() => fetchRequests('rejected')}
                                    >
                                        {t('pages.meetings.status_rejected') || 'Rejected'}
                                    </button>
                                </div>

                                <div className="mfrm-request-list">
                                    {loading ? (
                                        <ForwardRequestsListSkeleton />
                                    ) : requests.length === 0 ? (
                                        <div className="mfrm-empty-state">
                                            {t('common.empty_states.no_results') || 'No requests found'}
                                        </div>
                                    ) : (
                                        requests.map(request => (
                                            <div
                                                key={request.id}
                                                className={`mfrm-request-item ${selectedRequest?.id === request.id ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setSelectedRequest(request);
                                                    setRejectionReason('');
                                                }}
                                            >
                                                <div className="mfrm-request-item-content">
                                                    <div className="mfrm-request-names">
                                                        <span className="mfrm-requester-name">
                                                            {request.requester?.name || 'Unknown'}
                                                        </span>
                                                        <span className="mfrm-arrow">→</span>
                                                        <span className="mfrm-invitee-name">
                                                            {request.invitee?.name || 'Unknown'}
                                                        </span>
                                                    </div>
                                                    <span className={`mfrm-status-badge ${getStatusBadgeClass(request.status)}`}>
                                                        {getStatusLabel(request.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mfrm-right-panel">
                            {selectedRequest ? (
                                <div className="mfrm-detail-section">
                                    <h3 className="mfrm-detail-title">
                                        {t('pages.meetings.request_details') || 'Request Details'}
                                    </h3>

                                    <div className="mfrm-user-info-card">
                                        <h4 className="mfrm-card-title">
                                            {t('pages.meetings.requester') || 'Requester'}
                                        </h4>
                                        <div className="mfrm-user-info">
                                            {selectedRequest.requester?.avatar_url ? (
                                                <img src={selectedRequest.requester.avatar_url} alt={selectedRequest.requester.name} className="mfrm-user-avatar" />
                                            ) : (
                                                <div className="mfrm-user-avatar-placeholder">
                                                    {selectedRequest.requester?.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                            )}
                                            <div className="mfrm-user-details">
                                                <div className="mfrm-user-name">{selectedRequest.requester?.name}</div>
                                                <div className="mfrm-user-email">{selectedRequest.requester?.email}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mfrm-user-info-card">
                                        <h4 className="mfrm-card-title">
                                            {t('pages.meetings.invitee') || 'Invitee'}
                                        </h4>
                                        <div className="mfrm-user-info">
                                            {selectedRequest.invitee?.avatar_url ? (
                                                <img src={selectedRequest.invitee.avatar_url} alt={selectedRequest.invitee.name} className="mfrm-user-avatar" />
                                            ) : (
                                                <div className="mfrm-user-avatar-placeholder">
                                                    {selectedRequest.invitee?.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                            )}
                                            <div className="mfrm-user-details">
                                                <div className="mfrm-user-name">{selectedRequest.invitee?.name}</div>
                                                <div className="mfrm-user-email">{selectedRequest.invitee?.email}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mfrm-meta-info">
                                        <div className="mfrm-meta-row">
                                            <span className="mfrm-meta-label">
                                                {t('pages.meetings.scope') || 'Scope'}:
                                            </span>
                                            <span className="mfrm-meta-value">{getScopeLabel(selectedRequest.scope)}</span>
                                        </div>
                                        <div className="mfrm-meta-row">
                                            <span className="mfrm-meta-label">
                                                {t('pages.meetings.status') || 'Status'}:
                                            </span>
                                            <span className={`mfrm-meta-status ${getStatusBadgeClass(selectedRequest.status)}`}>
                                                {getStatusLabel(selectedRequest.status)}
                                            </span>
                                        </div>
                                    </div>

                                    {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                                        <div className="mfrm-rejection-reason">
                                            <h4 className="mfrm-card-title">
                                                {t('pages.meetings.rejection_reason') || 'Rejection Reason'}
                                            </h4>
                                            <p className="mfrm-reason-text">{selectedRequest.rejection_reason}</p>
                                        </div>
                                    )}

                                    {isOrganizerView && selectedRequest.status === 'pending' && (
                                        <div className="mfrm-reason-input-wrapper">
                                            <label className="mfrm-reason-label">
                                                {t('pages.meetings.rejection_reason') || 'Rejection Reason'} {t('common.actions.optional') || '(optional)'}
                                            </label>
                                            <textarea
                                                value={rejectionReason}
                                                onChange={(e) => setRejectionReason(e.target.value)}
                                                placeholder={t('pages.meetings.reason_placeholder') || 'Enter reason for rejection (optional)...'}
                                                className="mfrm-reason-textarea"
                                                rows="3"
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mfrm-empty-detail">
                                    <p>{t('pages.meetings.select_request') || 'Select a request to view details'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mfrm-footer">
                    <button
                        className="mfrm-footer-btn mfrm-footer-btn-secondary"
                        onClick={onClose}
                    >
                        {t('common.actions.close') || 'Close'}
                    </button>
                    {isOrganizerView && selectedRequest?.status === 'pending' && (
                        <div className="mfrm-footer-actions">
                            <button
                                className="mfrm-footer-btn mfrm-footer-btn-reject"
                                onClick={handleReject}
                            >
                                {t('pages.meetings.reject') || 'Reject'}
                            </button>
                            <button
                                className="mfrm-footer-btn mfrm-footer-btn-primary"
                                onClick={handleAccept}
                            >
                                {t('pages.meetings.accept') || 'Accept'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MeetingForwardRequestsModal;
